import {useEffect,useMemo,useRef,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AmazonFindCard from '../components/AmazonFindCard';
import PageShell from '../components/PageShell';
import useSiteContent from '../hooks/useSiteContent';
import {fallbackAmazonFinds} from '../data/amazonFinds';
import {
  removeRecord,
  syncRecordsByUrl
} from '../services/siteContentService';
import '../amazonFinds.css';

const {
  FiCalendar,
  FiChevronDown,
  FiPackage,
  FiSearch,
  FiShield
}=FiIcons;

const legacyDummyTitles=new Set([
  'Compact Creator Light',
  'Wireless Lavalier Microphone',
  'Monitor and Camera Desk Arm',
  'Mobile Video Creator Rig'
]);

const normalize=(value)=> String(value || '').trim().toLowerCase();
const normalizeUrl=(url)=> normalize(url).replace(/\/$/,'');

function isLegacyDummy(item) {
  return legacyDummyTitles.has(item.title);
}

function mergeAmazonFinds(sheetFinds) {
  const merged=new Map();

  sheetFinds
    .filter((item)=> !isLegacyDummy(item))
    .forEach((item)=> {
      const key=normalizeUrl(item.url) || normalize(item.title);
      if (key) merged.set(key,item);
    });

  fallbackAmazonFinds.forEach((item)=> {
    const key=normalizeUrl(item.url) || normalize(item.title);
    const databaseItem=merged.get(key);

    merged.set(key,{
      ...databaseItem,
      ...item,
      id: databaseItem?.id || item.id,
      click_count: databaseItem?.click_count || 0,
      created_at: databaseItem?.created_at,
      updated_at: databaseItem?.updated_at
    });
  });

  return [...merged.values()];
}

function dateValue(item) {
  const created=Date.parse(item.created_at || '');
  if (Number.isFinite(created)) return created;

  const updated=Date.parse(item.updated_at || '');
  return Number.isFinite(updated) ? updated : 0;
}

function sortAmazonFinds(items,sort) {
  return [...items].sort((a,b)=> {
    if (sort==='oldest') {
      const difference=dateValue(a) - dateValue(b);
      return difference || Number(a.order || 0) - Number(b.order || 0);
    }

    if (sort==='name-asc') {
      return String(a.title || '').localeCompare(String(b.title || ''));
    }

    if (sort==='name-desc') {
      return String(b.title || '').localeCompare(String(a.title || ''));
    }

    const difference=dateValue(b) - dateValue(a);
    return difference || Number(a.order || 0) - Number(b.order || 0);
  });
}

async function syncAmazonCatalog(sheetFinds) {
  await syncRecordsByUrl('amazonFinds',fallbackAmazonFinds);

  const dummyRecords=sheetFinds.filter(
    (item)=> isLegacyDummy(item) && item.id
  );

  await Promise.allSettled(
    dummyRecords.map((item)=> removeRecord('amazonFinds',item.id))
  );
}

export default function AmazonFinds() {
  const {
    data: sheetFinds,
    loading,
    error,
    refresh
  }=useSiteContent('amazonFinds',fallbackAmazonFinds);

  const [query,setQuery]=useState('');
  const [sort,setSort]=useState('newest');
  const [syncError,setSyncError]=useState('');
  const databaseSynced=useRef(false);

  const finds=useMemo(
    ()=> mergeAmazonFinds(sheetFinds),
    [sheetFinds]
  );

  useEffect(()=> {
    if (loading || databaseSynced.current) return;

    databaseSynced.current=true;
    setSyncError('');

    syncAmazonCatalog(sheetFinds)
      .then(()=> refresh())
      .catch((requestError)=> {
        databaseSynced.current=false;
        setSyncError(
          requestError.message || 'The catalog could not be synchronized.'
        );
      });
  },[loading,refresh,sheetFinds]);

  const visibleFinds=useMemo(()=> {
    const normalizedQuery=normalize(query);

    const matches=finds.filter((item)=> {
      const active=String(item.active || 'TRUE').toUpperCase()!=='FALSE';
      const searchable=[
        item.title,
        item.description,
        item.category,
        item.price_label
      ].join(' ').toLowerCase();

      return active && searchable.includes(normalizedQuery);
    });

    return sortAmazonFinds(matches,sort);
  },[finds,query,sort]);

  return (
    <PageShell>
      <section className="page-heading amazon-finds-heading">
        <span className="page-icon amazon-page-icon">
          <SafeIcon icon={FiPackage} />
        </span>
        <div>
          <p className="eyebrow">Dadda Cool picks</p>
          <h1>Amazon Finds</h1>
          <p>Compact gear, creator tools and useful everyday discoveries.</p>
        </div>
      </section>

      <aside className="amazon-disclosure">
        <SafeIcon icon={FiShield} />
        <p>
          Some links may be affiliate links. I may earn a small commission
          without any extra cost to you.
        </p>
      </aside>

      <div className="amazon-find-controls">
        <label className="search-box amazon-search">
          <SafeIcon icon={FiSearch} />
          <span className="sr-only">Search Amazon finds</span>
          <input
            value={query}
            onChange={(event)=> setQuery(event.target.value)}
            placeholder="Search selected finds"
          />
        </label>

        <label className="amazon-sort">
          <SafeIcon icon={FiCalendar} />
          <span className="sr-only">Sort Amazon finds</span>
          <select
            value={sort}
            onChange={(event)=> setSort(event.target.value)}
            aria-label="Sort Amazon finds"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name-asc">Name A–Z</option>
            <option value="name-desc">Name Z–A</option>
          </select>
          <SafeIcon icon={FiChevronDown} className="amazon-sort-chevron" />
        </label>
      </div>

      {(error || syncError) && (
        <p className="sr-only" role="status">
          The saved catalog is temporarily unavailable. Showing the current
          curated catalog.
        </p>
      )}

      <section className="amazon-finds-grid" aria-label="Selected Amazon finds">
        {visibleFinds.length ? (
          visibleFinds.map((item,index)=> (
            <AmazonFindCard
              key={item.id || item.url}
              item={item}
              index={index}
            />
          ))
        ) : (
          <div className="empty-state">
            No Amazon finds match your search.
          </div>
        )}
      </section>
    </PageShell>
  );
}